import { useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaHeartbeat,
  FaSyncAlt,
} from "react-icons/fa";
import PageLoading from "src/components/pageLoading";
import { useTheme } from "src/context/theme.context";
import { IntegrationHealthTable } from "../components/integration-health-table";
import { useGetIntegrationHealthQuery } from "../integration-health.service";
import { IntegrationHealthCursor } from "../types/integration-health.entity";

const IntegrationHealthPage = () => {
  const [pagination, setPagination] = useState<{
    pageIndex: number;
    offsets: (IntegrationHealthCursor | undefined)[];
  }>({
    pageIndex: 0,
    offsets: [undefined],
  });
  const { isDarkMode } = useTheme();
  const offset = pagination.offsets[pagination.pageIndex];
  const { data, isLoading, isFetching, isError, refetch } =
    useGetIntegrationHealthQuery({ offset });
  const isBusy = isLoading || isFetching;

  const previousPage = () => {
    setPagination((current) => ({
      ...current,
      pageIndex: current.pageIndex - 1,
    }));
  };

  const nextPage = () => {
    if (!data?.nextOffset) return;

    setPagination((current) => ({
      pageIndex: current.pageIndex + 1,
      offsets: [
        ...current.offsets.slice(0, current.pageIndex + 1),
        data.nextOffset,
      ],
    }));
  };

  return (
    <div className="page_container">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p
            className={`mb-1 text-sm font-medium ${isDarkMode ? "text-sky-300" : "text-sky-700"}`}
          >
            Technical operations
          </p>
          <h1
            className={`text-2xl font-semibold ${isDarkMode ? "text-slate-50" : "text-slate-900"}`}
          >
            Integration Health
          </h1>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isBusy}
          title="Refresh exceptions"
          className={`inline-flex h-10 w-10 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            isDarkMode
              ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
              : "border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50"
          }`}
        >
          <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
        </button>
      </div>

      {isLoading ? (
        <PageLoading />
      ) : isError ? (
        <div
          className={`border p-4 text-sm ${isDarkMode ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}
        >
          Could not load integration health. Please try again.
        </div>
      ) : data?.items.length ? (
        <>
          <div
            className={`mb-3 flex items-center justify-between text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}
          >
            <span>
              Showing {data.items.length} exception
              {data.items.length === 1 ? "" : "s"}
            </span>
            <span>Page {pagination.pageIndex + 1}</span>
          </div>
          <IntegrationHealthTable data={data.items} isDarkMode={isDarkMode} />
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={previousPage}
              disabled={pagination.pageIndex === 0 || isBusy}
              title="Previous page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={!data.nextOffset || isBusy}
              title="Next page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronRight />
            </button>
          </div>
        </>
      ) : (
        <div
          className={`border px-6 py-12 text-center ${isDarkMode ? "border-slate-700 bg-slate-900/50 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}
        >
          <FaHeartbeat
            className={`mx-auto mb-3 text-2xl ${isDarkMode ? "text-emerald-300" : "text-emerald-600"}`}
          />
          <p>No current integration exceptions.</p>
          {pagination.pageIndex > 0 && (
            <button
              type="button"
              onClick={previousPage}
              title="Previous page"
              className={`mt-4 inline-flex h-9 w-9 items-center justify-center rounded border transition-colors ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronLeft />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default IntegrationHealthPage;
